/**
 * useTranslatedTask
 *
 * Возвращает отображаемые title/description задачи на языке интерфейса.
 * Если перевод есть — показываем его; если нет — оригинал.
 * Пользователь может переключиться «Показать оригинал / Show translation».
 *
 * @param {object} task — объект задачи с полями title, description, original_lang, translations
 * @param {string} uiLang — текущий язык интерфейса ('ru', 'en', 'ar', ...)
 * @returns {object} { displayTitle, displayDescription, isTranslated, showingOriginal, toggleOriginal }
 */
import { useState, useMemo } from 'react';

export function useTranslatedTask(task, uiLang) {
  const [showOriginal, setShowOriginal] = useState(false);

  const result = useMemo(() => {
    if (!task) return null;

    const originalLang = task.original_lang;
    const translations = task.translations || {};

    // Нужен ли перевод: язык оригинала определён и отличается от языка интерфейса
    const needsTranslation = originalLang && originalLang !== uiLang;

    // Получаем перевод на язык интерфейса
    const translation = needsTranslation ? translations[uiLang] : null;
    const hasTranslation = !!(translation?.title || translation?.description);

    // isTranslated — показываем перевод (есть перевод и пользователь не просит оригинал)
    const isTranslated = hasTranslation && !showOriginal;

    return {
      displayTitle: isTranslated ? (translation.title || task.title) : task.title,
      displayDescription: isTranslated ? (translation.description || task.description) : task.description,
      isTranslated,
      hasTranslation,
      needsTranslation,
      showingOriginal: showOriginal,
      originalLang,
      toggleOriginal: () => setShowOriginal(v => !v),
    };
  }, [task, uiLang, showOriginal]);

  return result || {
    displayTitle: task?.title || '',
    displayDescription: task?.description || '',
    isTranslated: false,
    hasTranslation: false,
    needsTranslation: false,
    showingOriginal: false,
    originalLang: null,
    toggleOriginal: () => {},
  };
}
